import React, { useEffect, useState, useCallback } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Box,
  Grid,
  CircularProgress,
  MenuItem,
  Select,
  Paper,
  useTheme,
  TablePagination,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// ...existing code...
const severityColors = {
  INFO: '#17A2B8',
  LOW: '#28A745',
  MEDIUM: '#FFC107',
  MAJOR: '#FD7E14',
  CRITICAL: '#DC3545',
  BLOCKER: '#343A40',
};

const severityMap = {
  0: 'INFO',
  1: 'LOW',
  2: 'MEDIUM',
  3: 'MAJOR',
  4: 'CRITICAL',
  5: 'BLOCKER',
};

const CyberIssueList = ({ projectId, selectedSeverity }) => {
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10); // added

  const token = localStorage.getItem('access_token');
  useEffect(() => {
    if (selectedSeverity) {
      const upper = selectedSeverity.toUpperCase();
      if (Object.values(severityMap).includes(upper)) {
        setSeverityFilter(upper);
        setPage(0);
      } else {
        setSeverityFilter('ALL');
      }
    }
  }, [selectedSeverity]);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/flawfinder/flawfinder-cybersecurity-analysis-result?project_id=${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      const allVulns = data?.flawfinderResults?.flatMap(item => item.vulnerabilities || []);
      setVulnerabilities(allVulns);
    } catch (err) {
      console.error("Error fetching issues:", err);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredVulnerabilities = vulnerabilities.filter(vuln => {
    const sev = severityMap[vuln.Level];
    return severityFilter === 'ALL' || sev === severityFilter;
  });

  // reset page if filter or rowsPerPage changes or if current page is out of range
  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= filteredVulnerabilities.length) {
      setPage(0);
    }
  }, [filteredVulnerabilities.length, page, rowsPerPage]);

  // pagination helpers
  const paginatedVulnerabilities = filteredVulnerabilities.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Vulnerability Issues</Typography>

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Select
          value={severityFilter}
          onChange={(e) => { setSeverityFilter(e.target.value); setPage(0); }} // reset page when filter changes
          sx={{ width: 200 }}
        >
          <MenuItem value="ALL">All Severities</MenuItem>
          {Object.values(severityMap).map(label => (
            <MenuItem key={label} value={label}>{label}</MenuItem>
          ))}
        </Select>

        {/* optional quick summary */}
        <Typography variant="body2" color="textSecondary">
          {filteredVulnerabilities.length} issue(s)
        </Typography>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : filteredVulnerabilities.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
            No issues found for the selected severity.
          </Typography>
      ) : (
        <>
          {paginatedVulnerabilities.map((vuln, index) => {
            const severity = severityMap[vuln.Level] || 'UNKNOWN';
            const color = severityColors[severity] || '#888';
            return (
              <Accordion key={index + page * rowsPerPage} sx={{ mb: 1, borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item>
                      <Chip
                        label={severity}
                        sx={{
                          backgroundColor: color,
                          color: '#fff',
                          fontWeight: 'bold'
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Typography variant="body1">
                        {vuln.File}:{vuln.Line} - {vuln.Function || vuln.Warning}
                      </Typography>
                    </Grid>
                    <Grid item sx={{ marginLeft: 'auto' }}>
                      <Chip
                        label={vuln.Category || 'misc'}
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: 'lowercase' }}
                      />
                    </Grid>
                  </Grid>
                </AccordionSummary>

                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography><strong>File:</strong> {vuln.File}</Typography>
                      <Typography><strong>Line:</strong> {vuln.Line}</Typography>
                      <Typography><strong>Name:</strong> {vuln.Name || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography><strong>Rule:</strong> {vuln.RuleId || '-'}</Typography>
                      <Typography><strong>CWE:</strong> {vuln.CWEs || '-'}</Typography>
                      {vuln.HelpUri && (
                        <Typography>
                          <a href={vuln.HelpUri} target="_blank" rel="noopener noreferrer">
                            View Help
                          </a>
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography><strong>Warning:</strong> {vuln.Warning || '-'}</Typography>
                      <Typography><strong>Suggestion:</strong> {vuln.Suggestion || '-'}</Typography>
                    </Grid>
                    {vuln.context && (
                      <Grid item xs={12}>
                        <Typography><strong>Context:</strong></Typography>
                        <Paper sx={{ p: 1, bgcolor: theme.palette.mode === 'dark' ? '#424242' : '#f5f5f5' }}>
                          <pre style={{ margin: 0 }}>{vuln.context}</pre>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <TablePagination
              component="div"
              count={filteredVulnerabilities.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Rows per page"
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default CyberIssueList;